import { supabase } from '../config/supabase'
import { buildOfferPolicyFields } from '../utils/offerPackagePolicy'

const OFFER_SELECT_BASE = `
  id,
  salon_id,
  title,
  subtitle,
  cta_label,
  image_url,
  discount_percent,
  sort_order,
  is_active,
  starts_at,
  ends_at,
  total_days,
  parent_id,
  hero_offer_services (
    sort_order,
    service_id,
    day_number,
    services (
      id,
      name,
      duration_minutes,
      price_from,
      category_id,
      description,
      is_active,
      base_currency,
      service_categories ( name )
    )
  )
`

const OFFER_SELECT_POLICY = `
  id,
  salon_id,
  title,
  subtitle,
  cta_label,
  image_url,
  discount_percent,
  sort_order,
  is_active,
  starts_at,
  ends_at,
  total_days,
  parent_id,
  allowed_payment_methods,
  reschedule_allowed,
  cancel_allowed,
  reschedule_policy_override,
  cancel_policy_override,
  payment_methods,
  allow_reschedule,
  allow_cancel,
  override_reschedule_policy,
  override_cancel_policy,
  hero_offer_services (
    sort_order,
    service_id,
    day_number,
    services (
      id,
      name,
      duration_minutes,
      price_from,
      category_id,
      description,
      is_active,
      base_currency,
      service_categories ( name )
    )
  )
`

let offerSelectSupportsPolicy = true

async function fetchHeroOfferRows(buildQuery) {
  const selectCols = offerSelectSupportsPolicy ? OFFER_SELECT_POLICY : OFFER_SELECT_BASE
  let { data, error } = await buildQuery(selectCols)
  if (
    error &&
    offerSelectSupportsPolicy &&
    /payment_methods|allowed_payment_methods|allow_reschedule|allow_cancel|reschedule_allowed|cancel_allowed|override_|policy_override/i.test(
      error.message || ''
    )
  ) {
    offerSelectSupportsPolicy = false
    ;({ data, error } = await buildQuery(OFFER_SELECT_BASE))
  }
  return { data, error }
}

const PARENT_SELECT = 'id, salon_id, title, subtitle, image_url, sort_order, starts_at, ends_at, is_active, parent_id'

const CACHE_TTL_MS = 5 * 60 * 1000
const cache = new Map()
const inflight = new Map()

function withCache(key, loader) {
  const entry = cache.get(key)
  if (entry && Date.now() - entry.at <= CACHE_TTL_MS) {
    return Promise.resolve(entry.value)
  }
  cache.delete(key)

  const pending = inflight.get(key)
  if (pending) return pending

  const promise = loader()
    .then((result) => {
      if (!result.error) cache.set(key, { at: Date.now(), value: result })
      return result
    })
    .finally(() => inflight.delete(key))

  inflight.set(key, promise)
  return promise
}

export function clearHeroOfferCache() {
  cache.clear()
  inflight.clear()
}

export function mapHeroOffer(row) {
  if (!row) return null
  const links = [...(row.hero_offer_services || [])].sort(
    (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
  )
  const services = links.map((link) => ({
    ...(link.services || {}),
    day_number: link.day_number || 1
  })).filter((s) => s.id)
  return {
    id: row.id,
    salon_id: row.salon_id,
    parent_id: row.parent_id || null,
    title: row.title,
    subtitle: row.subtitle,
    cta_label: row.cta_label || 'Book Now',
    image_url: row.image_url,
    discount_percent: Number(row.discount_percent) || 0,
    sort_order: row.sort_order || 0,
    is_active: row.is_active !== false,
    starts_at: row.starts_at,
    ends_at: row.ends_at,
    total_days: Number(row.total_days) || 1,
    ...buildOfferPolicyFields(row),
    services,
    serviceIds: services.map((service) => service.id),
  }
}

/** Visible until end date — upcoming (future starts_at) offers stay listed. */
export function isOfferLive(offer) {
  if (!offer || offer.is_active === false) return false
  const now = Date.now()
  if (offer.ends_at && new Date(offer.ends_at).getTime() < now) return false
  return true
}

export function getActiveHeroOffers(salonId) {
  if (!salonId) return Promise.resolve({ data: [], error: null })
  // v3: parent stays if any child package is still live (e.g. Dec deal under Sep campaign)
  return withCache(`active:v3:${salonId}`, async () => {
    try {
      const [{ data: parents, error: parentError }, { data: children, error: childError }] =
        await Promise.all([
          fetchHeroOfferRows((cols) =>
            supabase
              .from('hero_offers')
              .select(cols)
              .eq('salon_id', salonId)
              .eq('is_active', true)
              .is('parent_id', null)
              .order('sort_order', { ascending: true })
          ),
          supabase
            .from('hero_offers')
            .select('id, parent_id, is_active, starts_at, ends_at')
            .eq('salon_id', salonId)
            .eq('is_active', true)
            .not('parent_id', 'is', null),
        ])

      if (parentError) throw parentError
      if (childError) console.warn('Could not load child offer dates for hero:', childError)

      const liveChildParentIds = new Set(
        (children || [])
          .filter((row) => isOfferLive(row))
          .map((row) => String(row.parent_id))
      )

      const offers = (parents || [])
        .map(mapHeroOffer)
        .filter((offer) => {
          if (!offer) return false
          if (isOfferLive(offer)) return true
          return liveChildParentIds.has(String(offer.id))
        })

      return { data: offers, error: null }
    } catch (error) {
      console.error('Error fetching hero offers:', error)
      return { data: [], error }
    }
  })
}

/**
 * Lightweight parent-offer list used to resolve a campaign slug to its offer.
 * Only the fields the campaign header renders are selected.
 */
export function getParentOffers(salonId) {
  if (!salonId) return Promise.resolve({ data: [], error: null })
  return withCache(`parents:v3:${salonId}`, async () => {
    try {
      const { data, error } = await supabase
        .from('hero_offers')
        .select(PARENT_SELECT)
        .eq('salon_id', salonId)
        .eq('is_active', true)
        .is('parent_id', null)
        .order('sort_order', { ascending: true })

      if (error) throw error

      const { data: children } = await supabase
        .from('hero_offers')
        .select('id, parent_id, is_active, starts_at, ends_at')
        .eq('salon_id', salonId)
        .eq('is_active', true)
        .not('parent_id', 'is', null)

      const liveChildParentIds = new Set(
        (children || [])
          .filter((row) => isOfferLive(row))
          .map((row) => String(row.parent_id))
      )

      const parents = (data || []).filter((row) => {
        if (isOfferLive(row)) return true
        return liveChildParentIds.has(String(row.id))
      })

      return { data: parents, error: null }
    } catch (error) {
      console.error('Error fetching parent offers:', error)
      return { data: [], error }
    }
  })
}

export function getChildOffers(parentId) {
  if (!parentId) return Promise.resolve({ data: [], error: null })
  return withCache(`child:v3:${parentId}`, async () => {
    try {
      const { data, error } = await fetchHeroOfferRows((cols) =>
        supabase
          .from('hero_offers')
          .select(cols)
          .eq('parent_id', parentId)
          .eq('is_active', true)
          .order('sort_order', { ascending: true })
      )

      if (error) throw error
      const offers = (data || []).map(mapHeroOffer).filter((offer) => offer && isOfferLive(offer))
      return { data: offers, error: null }
    } catch (error) {
      console.error('Error fetching child offers:', error)
      return { data: [], error }
    }
  })
}
